import React from 'react';
import { Check, ArrowRight, Users, Clock, Presentation, Wifi, Zap, PenTool } from 'lucide-react';
import { PageTransition } from '../../components/layout/PageTransition';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { DecorativeShape } from '../../components/DecorativeShape';
import { Animate } from '../../components/ui/Animate';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
export function BetterOnlineWorkshops() {
  const {
    t
  } = useLanguage();
  const benefits = t('betterOnlineWorkshops.benefits') as string[];
  const sessions = t('betterOnlineWorkshops.sessions') as Array<{
    title: string;
    desc: string;
    topics: string[];
    isHomework?: boolean;
  }>;
  const forWhomItems = t('betterOnlineWorkshops.forWhomItems') as string[];
  const forWhomIcons = [<Presentation className="text-purple-600 shrink-0" size={20} data-id="element-552" />, <Zap className="text-purple-600 shrink-0" size={20} data-id="element-553" />, <Users className="text-purple-600 shrink-0" size={20} data-id="element-554" />, <PenTool className="text-purple-600 shrink-0" size={20} data-id="element-555" />];
  return <PageTransition data-id="element-556">
      <div className="pt-12 pb-24" data-id="element-557">
        <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-558">
          <DecorativeShape type="circle" color="bg-purple-100" className="top-0 right-0 w-[400px] h-[400px] translate-x-1/4 -translate-y-1/4 -z-10" data-id="element-559" />
          <DecorativeShape type="blob" color="bg-fuchsia-100" className="bottom-0 left-0 w-64 h-64 -translate-x-1/4 translate-y-1/4 -z-10" data-id="element-560" />
          <Animate className="text-center max-w-4xl mx-auto" data-id="element-561">
            <span className="inline-block px-4 py-2 bg-purple-50 text-purple-600 font-medium rounded-full text-sm mb-6" data-id="element-562">
              {t('betterOnlineWorkshops.badge')}
            </span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight" data-id="element-563">
              {t('betterOnlineWorkshops.title')} <br data-id="element-564" />
              <span className="text-purple-600" data-id="element-565">
                {t('betterOnlineWorkshops.titleHighlight')}
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed" data-id="element-566">
              {t('betterOnlineWorkshops.subtitle')}
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm font-medium text-gray-500 mb-10" data-id="element-567">
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100" data-id="element-568">
                <Clock size={18} className="text-purple-600" data-id="element-569" />
                <span data-id="element-570">{t('betterOnlineWorkshops.duration')}</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100" data-id="element-571">
                <Presentation size={18} className="text-purple-600" data-id="element-572" />
                <span data-id="element-573">{t('betterOnlineWorkshops.audience')}</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100" data-id="element-574">
                <Wifi size={18} className="text-purple-600" data-id="element-575" />
                <span data-id="element-576">{t('betterOnlineWorkshops.online')}</span>
              </div>
            </div>
            <Link to="/contact" data-id="element-577">
              <Button className="bg-purple-600 hover:bg-purple-700 text-white shadow-purple-200" size="lg" rightIcon={<ArrowRight size={20} data-id="element-579" />} data-id="element-578">
                {t('betterOnlineWorkshops.bookCta')}
              </Button>
            </Link>
          </Animate>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24" data-id="element-580">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-581">
            <div className="lg:col-span-2 space-y-12" data-id="element-582">
              <Animate data-id="element-583">
                <h2 className="text-3xl font-bold mb-6 text-charcoal" data-id="element-584">
                  {t('betterOnlineWorkshops.aboutTitle')}
                </h2>
                <div className="prose prose-lg text-gray-600" data-id="element-585">
                  <p className="mb-4" data-id="element-586">{t('betterOnlineWorkshops.aboutP1')}</p>
                  <p data-id="element-587">{t('betterOnlineWorkshops.aboutP2')}</p>
                </div>
              </Animate>
              <Animate delay={0.1} data-id="element-588">
                <h3 className="text-2xl font-bold mb-6 text-charcoal" data-id="element-589">
                  {t('betterOnlineWorkshops.learnTitle')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-590">
                  {benefits.map((benefit: string, index: number) => <div key={index} className="flex items-start gap-3" data-id="element-591">
                      <div className="mt-1 p-1 rounded-full bg-purple-50 text-purple-600 flex-shrink-0" data-id="element-592">
                        <Check size={14} strokeWidth={3} data-id="element-593" />
                      </div>
                      <span className="text-gray-700" data-id="element-594">{benefit}</span>
                    </div>)}
                </div>
              </Animate>
              <Animate delay={0.15} data-id="element-595">
                <h3 className="text-2xl font-bold mb-6 text-charcoal" data-id="element-596">
                  {t('betterOnlineWorkshops.structureTitle')}
                </h3>
                <div className="space-y-6" data-id="element-597">
                  {sessions.map((session, index) => <Card key={index} className={`p-6 border-l-4 ${session.isHomework ? 'border-yellow-400 bg-yellow-50' : 'border-purple-600 bg-white'}`} data-id="element-598">
                      <div className="flex items-start justify-between mb-2" data-id="element-599">
                        <h4 className="text-xl font-bold text-charcoal" data-id="element-600">
                          {session.title}
                        </h4>
                        {session.isHomework && <span className="bg-yellow-200 text-yellow-800 text-xs font-bold px-2 py-1 rounded-full" data-id="element-601">
                            {t('betterOnlineWorkshops.homework')}
                          </span>}
                      </div>
                      <p className="text-gray-600 mb-4" data-id="element-602">{session.desc}</p>
                      <div className={`${session.isHomework ? 'bg-white' : 'bg-purple-50'} p-4 rounded-xl`} data-id="element-603">
                        <p className={`text-sm font-bold ${session.isHomework ? 'text-yellow-800' : 'text-purple-800'} mb-2`} data-id="element-604">
                          {t('betterOnlineWorkshops.topicsLabel')}
                        </p>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-605">
                          {session.topics.map((topic: string, i: number) => <li key={i} className="flex items-center gap-2 text-sm text-gray-700" data-id="element-606">
                              <div className={`w-1.5 h-1.5 rounded-full ${session.isHomework ? 'bg-yellow-500' : 'bg-purple-600'}`} data-id="element-607" />
                              {topic}
                            </li>)}
                        </ul>
                      </div>
                    </Card>)}
                </div>
              </Animate>
            </div>
            <div className="lg:col-span-1" data-id="element-608">
              <div className="sticky top-24 space-y-8" data-id="element-609">
                <Card className="p-8 bg-white shadow-xl border-t-4 border-purple-600" data-id="element-610">
                  <h3 className="text-xl font-bold mb-6 text-charcoal" data-id="element-611">
                    {t('betterOnlineWorkshops.forWhom')}
                  </h3>
                  <ul className="space-y-4 mb-8" data-id="element-612">
                    {forWhomItems.map((item: string, i: number) => <li key={i} className="flex items-start gap-3" data-id="element-613">
                        {forWhomIcons[i]}
                        <span className="text-gray-600" data-id="element-614">{item}</span>
                      </li>)}
                  </ul>
                  <Link to="/contact" className="block" data-id="element-615">
                    <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white shadow-purple-200" rightIcon={<ArrowRight size={18} data-id="element-617" />} data-id="element-616">
                      {t('betterOnlineWorkshops.inquiry')}
                    </Button>
                  </Link>
                </Card>
                <div className="bg-purple-50 rounded-3xl p-8 text-center" data-id="element-618">
                  <div className="w-16 h-16 mx-auto bg-white rounded-full flex items-center justify-center shadow-md mb-4 text-purple-600" data-id="element-619">
                    <Users size={32} data-id="element-620" />
                  </div>
                  <h4 className="font-bold text-lg mb-2" data-id="element-621">
                    {t('betterOnlineWorkshops.groupTitle')}
                  </h4>
                  <p className="text-gray-600 text-sm mb-4" data-id="element-622">
                    {t('betterOnlineWorkshops.groupDesc')}
                  </p>
                  <Link to="/contact" className="text-purple-600 font-bold hover:underline" data-id="element-623">
                    {t('betterOnlineWorkshops.contactLink')}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 text-center" data-id="element-624">
          <Animate data-id="element-625">
            <Card className="p-12 bg-gradient-to-br from-purple-50 to-fuchsia-50 border-0" data-id="element-626">
              <h2 className="text-3xl font-bold mb-4" data-id="element-627">
                {t('betterOnlineWorkshops.ctaTitle')}
              </h2>
              <p className="text-gray-600 mb-8 max-w-xl mx-auto" data-id="element-628">
                {t('betterOnlineWorkshops.ctaDesc')}
              </p>
              <Link to="/contact" data-id="element-629">
                <Button className="bg-purple-600 hover:bg-purple-700 text-white shadow-purple-200" rightIcon={<ArrowRight size={18} data-id="element-631" />} data-id="element-630">
                  {t('betterOnlineWorkshops.ctaCta')}
                </Button>
              </Link>
            </Card>
          </Animate>
        </section>
      </div>
    </PageTransition>;
}